
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "sonner"
import { useState } from "react"
import { TransactionData } from "@/types/types"
import { useCurrency } from "@/contexts/CurrencyContext"

/**
 * Charge utile de mise à jour d'un paiement.
 * Les clés reprennent le nom des colonnes de la table `payments` : le dialogue
 * écrit directement en base via PostgREST, qui rejette toute clé inconnue.
 */
export interface PaymentUpdatePayload {
  amount?: number
  status?: string
  payment_method?: string
  description?: string
}

interface TransactionDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  transaction: TransactionData
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
  updateTransaction?: (id: string, data: PaymentUpdatePayload) => Promise<boolean>
  deleteTransaction?: (id: string) => Promise<boolean>
}

export function TransactionDetailsDialog({
  open,
  onOpenChange,
  transaction,
  onUpdateSuccess = () => {},
  onDeleteSuccess = () => {},
  updateTransaction = async () => false,
  deleteTransaction = async () => false,
}: TransactionDetailsDialogProps) {
  const { formatCurrency } = useCurrency()
  const [isEditing, setIsEditing] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [amount, setAmount] = useState(transaction?.amount || "")
  const [status, setStatus] = useState(transaction?.status || "")
  const [paymentMethod, setPaymentMethod] = useState(transaction?.paymentMethod || "")
  const [selectedTransactionId, setSelectedTransactionId] = useState(transaction?.id || null)

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleCancelClick = () => {
    setIsEditing(false)
    setAmount(transaction?.amount || "")
    setStatus(transaction?.status || "")
    setPaymentMethod(transaction?.paymentMethod || "")
  }

  // La devise vient des réglages de la salle : l'ancien Intl en `en-US`/`USD`
  // affichait « $300.00 » sur des montants en dinars.
  const formatAmount = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (!Number.isFinite(numAmount)) return String(amount ?? "—");
    return formatCurrency(numAmount);
  };

  const handleUpdateTransaction = async () => {
    if (selectedTransactionId) {
      const parsedAmount = parseFloat(amount.toString())
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        toast.error("Montant invalide")
        return
      }
      try {
        setIsUpdating(true)
        // La colonne s'appelle `payment_method` : envoyer `paymentMethod`
        // faisait rejeter PostgREST, donc *toute* modification échouait,
        // y compris un simple changement de montant.
        const result = await updateTransaction(selectedTransactionId.toString(), {
          amount: parsedAmount,
          status: status,
          payment_method: paymentMethod,
        })

        if (result) {
          toast.success("Transaction updated successfully")
          setIsEditing(false)
          onUpdateSuccess()
        } else {
          // On reste en mode édition pour que la correction puisse être rejouée.
          toast.error("Failed to update transaction")
        }
      } catch (error) {
        console.error("Error updating transaction:", error)
        toast.error("An error occurred while updating the transaction")
      } finally {
        setIsUpdating(false)
      }
    }
  }

  const handleDeleteTransaction = async () => {
    if (selectedTransactionId) {
      try {
        setIsDeleting(true);
        const result = await deleteTransaction(String(selectedTransactionId));
        
        if (result) {
          toast.success("Transaction deleted successfully");
          onDeleteSuccess();
        } else {
          toast.error("Failed to delete transaction");
        }
      } catch (error) {
        console.error("Error deleting transaction:", error);
        toast.error("An error occurred while deleting the transaction");
      } finally {
        setIsDeleting(false);
        setIsDeleteDialogOpen(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Transaction Details</DialogTitle>
          <DialogDescription>
            View and manage details about this transaction.
          </DialogDescription>
        </DialogHeader>
        {transaction ? (
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-muted-foreground">Amount</Label>
                {isEditing ? (
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                ) : (
                  <div className="font-medium">{formatAmount(transaction.amount)}</div>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground">Date</Label>
                <div className="font-medium">{transaction.date}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-muted-foreground">Payment Method</Label>
                <div className="font-medium">{transaction.paymentMethod || "Credit Card"}</div>
              </div>
              <div>
                <Label className="text-muted-foreground">Status</Label>
                {isEditing ? (
                  <Select onValueChange={setStatus} defaultValue={status}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder={status} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="font-medium">{transaction.status}</div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 mb-4">
              <div>
                <Label className="text-muted-foreground">Client</Label>
                <div className="font-medium">{transaction.client}</div>
              </div>
              <div>
                <Label className="text-muted-foreground">Type</Label>
                <div className="font-medium">{transaction.type}</div>
              </div>
              <div>
                <Label className="text-muted-foreground">Description</Label>
                <div className="font-medium">{transaction.description}</div>
              </div>
            </div>
          </div>
        ) : (
          <div>Loading transaction details...</div>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(true)}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete this
                transaction from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteTransaction}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <div className="flex justify-end gap-2">
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={handleCancelClick}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={handleUpdateTransaction}
                disabled={isUpdating}
              >
                {isUpdating ? "Updating..." : "Save"}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={handleEditClick}
              disabled={isUpdating}
            >
              Edit
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
